import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Link as LinkIcon, Gift } from "lucide-react";
import { useEffect } from "react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteCode = params.get("invite") || "";
  const [inviter, setInviter] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!inviteCode) return;
    api.get(`/invites/${inviteCode}`)
      .then(r => setInviter(r.data.inviter_name))
      .catch(e => setInviteError(e.response?.data?.detail || "Invite code is not valid"));
  }, [inviteCode]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form };
    if (inviteCode && !inviteError) payload.invite_code = inviteCode;
    const r = await register(payload);
    setLoading(false);
    if (r.ok) { toast.success("Welcome to Access Local!"); navigate("/dashboard"); }
    else toast.error(r.error);
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Card className="border-2 border-gray-900 rounded-xl">
        <CardContent className="p-8">
          <h1 className="font-heading text-3xl font-bold text-gray-900">Join Access Local</h1>
          <p className="mt-2 text-gray-700">Sign up as a member to nominate accessible places and save your favourites.</p>
          {inviteCode && !inviteError && inviter && (
            <div className="mt-6 rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="invite-context-banner">
              <div className="flex items-start gap-2">
                <Gift className="w-4 h-4 mt-0.5 text-emerald-700"/>
                <div><strong>{inviter}</strong> invited you to Access Local. Complete sign-up to accept.</div>
              </div>
            </div>
          )}
          {inviteCode && inviteError && (
            <div className="mt-6 rounded-lg border-2 border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="invite-context-error">
              {inviteError} You can still register without the invite.
            </div>
          )}
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="name" className="text-lg font-medium">Full name</Label>
              <Input id="name" required value={form.name} onChange={e => set("name", e.target.value)}
                     className="mt-2 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="register-name"/>
            </div>
            <div>
              <Label htmlFor="email" className="text-lg font-medium">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={e => set("email", e.target.value)}
                     className="mt-2 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="register-email"/>
            </div>
            <div>
              <Label htmlFor="password" className="text-lg font-medium">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={e => set("password", e.target.value)}
                     className="mt-2 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="register-password"/>
              <p className="mt-1 text-xs text-gray-500">Minimum 6 characters.</p>
            </div>
            <div className="rounded-lg border-2 border-blue-100 bg-blue-50 p-4 text-sm text-gray-800">
              <div className="flex items-start gap-2">
                <LinkIcon className="w-4 h-4 text-blue-700 mt-0.5" />
                <div>
                  <strong className="font-semibold">Want to become an inspector?</strong> Inspector accounts are approved by our team.
                  Please <Link to="/contact" className="text-blue-700 font-medium underline">get in touch</Link> and we'll be back in touch.
                </div>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="register-submit">
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-gray-700">
            Already a member? <Link to="/login" className="text-blue-700 font-medium hover:underline">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
